import React, { useState, useEffect, useRef, useCallback } from 'react';
import { ArrowRight, MapPin, Users, Truck, Award } from 'lucide-react';
import IndiaMap from '../../assets/about/growthmap.png';

const GrowthSection = () => {
    const [isVisible, setIsVisible] = useState(false);
    const [imageLoaded, setImageLoaded] = useState(false);
    const [imageError, setImageError] = useState(false);
    const sectionRef = useRef(null);

    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setIsVisible(true);
                }
            },
            { threshold: 0.1 }
        );

        if (sectionRef.current) {
            observer.observe(sectionRef.current);
        }

        return () => observer.disconnect();
    }, []);

    const handleImageLoad = useCallback(() => {
        setImageLoaded(true);
    }, []);

    const handleImageError = useCallback(() => {
        setImageError(true);
        console.error('Failed to load India map image');
    }, []);

    const stats = [
        { icon: MapPin, value: '15+', label: 'Cities Covered', color: 'text-blue-600' },
        { icon: Users, value: '500+', label: 'Happy Clients', color: 'text-green-600' },
        { icon: Truck, value: '1,000+', label: 'Deliveries Monthly', color: 'text-orange-600' },
        { icon: Award, value: '7+', label: 'Years Experience', color: 'text-purple-600' }
    ];

    return (
        <section
            ref={sectionRef}
            className="w-full bg-white pt-20 pb-8 overflow-hidden"
            aria-labelledby="growth-section-title"
            role="region"
        >
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="grid lg:grid-cols-2 gap-16 items-center">

                    {/* Left Content */}
                    <div className="space-y-8">
                        {/* Small tag */}
                        <div
                            className={`transform transition-all duration-1000 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'
                                }`}
                        >
                            <span className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-orange-50 text-orange-700 font-medium border border-orange-200">
                                <span className="w-2 h-2 bg-orange-500 rounded-full mr-2"></span>
                                India's Leading Logistics Partner
                            </span>
                        </div>

                        {/* Main heading */}
                        <div
                            className={`transform transition-all duration-1000 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'
                                }`}
                            style={{ transitionDelay: '0.2s' }}
                        >
                            <h2
                                id="growth-section-title"
                                className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-800 leading-tight"
                            >
                                About <span className="text-[#F05A28]">RoadExpress</span>
                            </h2>
                        </div>

                        {/* Description */}
                        <div
                            className={`transform transition-all duration-1000 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'
                                }`}
                            style={{ transitionDelay: '0.4s' }}
                        >
                            <p className="text-lg text-gray-600 leading-relaxed max-w-xl mb-6">
                                Founded in 2018, RoadExpress is one of the fastest-growing companies in the logistics sector, with headquarters in Patna and Gurugram. The company is backed by both central and state governments, as well as leading investors and institutions across India.
                                <br />
                                RoadExpress is revolutionizing India’s logistics industry through a tech-enabled marketplace that connects transporters and fleet owners with high-value projects and trips. By leveraging artificial intelligence, the platform enhances vehicle utilization, reduces empty miles, and ensures transparent, cost-efficient freight movement, particularly across intra-state routes.
                                <br />
                                Powered by intelligent matching algorithms, real-time pricing, and a trusted network of verified fleet owners, RoadExpress delivers streamlined logistics operations. The platform offers businesses scalable, efficient, and reliable transportation solutions tailored to their needs.
                            </p>

                        </div>

                        {/* Stats Grid */}
                        <div
                            className={`transform transition-all duration-1000 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'
                                }`}
                            style={{ transitionDelay: '0.5s' }}
                        >
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 py-6">
                                {stats.map((stat, index) => {
                                    const IconComponent = stat.icon;
                                    return (
                                        <div key={index} className="text-center">
                                            <div className={`${stat.color} mb-2 flex justify-center`}>
                                                <IconComponent size={24} />
                                            </div>
                                            <div className="font-bold text-xl text-gray-800">{stat.value}</div>
                                            <div className="text-sm text-gray-600">{stat.label}</div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>

                        {/* CTA Buttons */}
                        <div
                            className={`transform transition-all duration-1000 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'
                                }`}
                            style={{ transitionDelay: '0.6s' }}
                        >
                            <div className="flex flex-col sm:flex-row gap-4">
                                <button
                                    className="group border-2 border-gray-300 hover:border-orange-500 text-gray-700 hover:text-[#F05A28] px-8 py-4 rounded-lg font-semibold transition-all duration-300 flex items-center justify-center space-x-3 focus:outline-none focus:ring-4 focus:ring-gray-300/30"
                                    aria-label="View our career opportunities"
                                >
                                    <span>View Careers</span>
                                    <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-300" />
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Right Content - Map */}
                    <div
                        className={`relative transform transition-all duration-1000 ${isVisible ? 'translate-x-0 opacity-100' : 'translate-x-[50px] opacity-0'
                            }`}
                        style={{ transitionDelay: '0.8s' }}
                    >
                        <div className="relative">
                            {!imageError ? (
                                <div className="relative">
                                    {/* Loading skeleton */}
                                    {!imageLoaded && (
                                        <div className="absolute inset-0 bg-gray-200 rounded-2xl animate-pulse"></div>
                                    )}
                                    <img
                                        src={IndiaMap}
                                        alt="RoadExpress logistics network coverage map showing service areas across 500+ cities in India including major metros, tier-2 and tier-3 cities"
                                        className={`w-full h-auto rounded-2xl transition-opacity duration-500 ${imageLoaded ? 'opacity-100' : 'opacity-0'
                                            }`}
                                        onLoad={handleImageLoad}
                                        onError={handleImageError}
                                        loading="lazy"
                                        width="600"
                                        height="400"
                                    />
                                </div>
                            ) : (
                                <div className="w-full h-96 flex items-center justify-center bg-gradient-to-br from-orange-50 to-orange-100 rounded-2xl shadow-lg">
                                    <div className="text-center">
                                        <div className="text-6xl mb-4">🇮🇳</div>
                                        <div className="text-xl font-semibold text-gray-800">India Coverage Network</div>
                                        <div className="text-sm text-gray-600 mt-2">Serving 500+ cities nationwide</div>
                                        <div className="text-xs text-gray-500 mt-1">From metros to tier-3 cities</div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                </div>
            </div>

            {/* Enhanced SEO Structured Data */}
            <script
                type="application/ld+json"
                dangerouslySetInnerHTML={{
                    __html: JSON.stringify({
                        "@context": "https://schema.org",
                        "@type": "Organization",
                        "name": "RoadExpress",
                        "description": "India's leading logistics technology platform providing reliable transportation solutions across 500+ cities",
                        "foundingDate": "2018",
                        "areaServed": {
                            "@type": "Country",
                            "name": "India"
                        },
                        "serviceType": ["Logistics Services", "Transportation", "Supply Chain Management", "Freight Services"],
                        "aggregateRating": {
                            "@type": "AggregateRating",
                            "ratingValue": "4.8",
                            "ratingCount": "5000",
                            "bestRating": "5",
                            "worstRating": "1"
                        },
                        "address": {
                            "@type": "PostalAddress",
                            "addressCountry": "IN"
                        },
                        "sameAs": [],
                        "knowsAbout": ["Logistics", "Transportation", "Supply Chain", "Freight Management", "Last Mile Delivery"]
                    })
                }}
            />
        </section>
    );
};

export default GrowthSection;